import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNotify } from 'react-admin';

import { requireAuth, requirePartner } from '@src/providers/dataProvider/shared';

import { fetchSftpFolders } from './fetchSftpFolders';
import { fetchSftpFolderContents } from './fetchSftpFolderContents';
import {
  createSftpSubfolder,
  deleteSftpFile,
  deleteSftpSubfolder,
  getSftpFileDownloadUrls,
  getSftpFileUploadUrl,
  moveSftpFile,
  moveSftpSubfolder,
  renameSftpFile,
  renameSftpSubfolder,
} from './mutations';
import { uploadToS3 } from '@src/apis/reware/catalog/uploadToS3';
import type { FileBrowserItem } from './types';

const joinPath = (parentPath: string | undefined, name: string): string =>
  parentPath ? `${parentPath}/${name}` : name;

/**
 * Drives the Files & Folders browser: reads the current `entry`/`path` from
 * the URL (so navigation is back/forward-able and bookmarkable), loads
 * either the top-level SFTP folder entries or a folder's contents, and
 * exposes the file/folder mutation actions.
 */
export const useFileBrowser = () => {
  const notify = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const entry = searchParams.get('entry') || undefined;
  const path = searchParams.get('path') || undefined;

  const [items, setItems] = useState<FileBrowserItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number, total: number } | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = requireAuth();
      const partnerCode = requirePartner().code;

      if (!entry) {
        const folders = await fetchSftpFolders({ token, partnerCode });
        setItems(folders.map(name => ({ type: 'folder' as const, name })));
        return;
      }

      const contents = await fetchSftpFolderContents({ token, partnerCode, entry, path });
      setItems([
        ...contents.folders.map(name => ({ type: 'folder' as const, name })),
        ...contents.files.map(file => ({ ...file, type: 'file' as const })),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files.');
    } finally {
      setIsLoading(false);
    }
  }, [entry, path]);

  useEffect(() => {
    void load();
  }, [load]);

  const breadcrumb = useMemo(() => {
    const segments = [{ label: 'Home', entry: undefined as string | undefined, path: undefined as string | undefined }];
    if (entry) {
      segments.push({ label: entry, entry, path: undefined });
      const parts = (path ?? '').split('/').filter(Boolean);
      parts.forEach((part, index) => {
        segments.push({ label: part, entry, path: parts.slice(0, index + 1).join('/') });
      });
    }
    return segments;
  }, [entry, path]);

  const navigateTo = useCallback((nextEntry?: string, nextPath?: string) => {
    const params: Record<string, string> = {};
    if (nextEntry) params.entry = nextEntry;
    if (nextPath) params.path = nextPath;
    setSearchParams(params);
  }, [setSearchParams]);

  const navigateInto = useCallback((item: FileBrowserItem) => {
    if (item.type !== 'folder') return;
    if (!entry) {
      navigateTo(item.name, undefined);
    } else {
      navigateTo(entry, joinPath(path, item.name));
    }
  }, [entry, path, navigateTo]);

  const createFolder = useCallback(async (name: string) => {
    if (!entry) return;
    try {
      const token = requireAuth();
      const partnerCode = requirePartner().code;
      await createSftpSubfolder({ token, partnerCode, entry, path: joinPath(path, name) });
      notify('Folder created', { type: 'success' });
      await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to create folder.', { type: 'error' });
    }
  }, [entry, path, notify, load]);

  const renameItem = useCallback(async (item: FileBrowserItem, newName: string) => {
    if (!entry) return;
    try {
      const token = requireAuth();
      const partnerCode = requirePartner().code;
      if (item.type === 'folder') {
        await renameSftpSubfolder({ token, partnerCode, entry, path: joinPath(path, item.name), newName });
      } else {
        await renameSftpFile({ token, partnerCode, entry, path, filename: item.name, newFilename: newName });
      }
      notify('Renamed', { type: 'success' });
      await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to rename.', { type: 'error' });
    }
  }, [entry, path, notify, load]);

  const deleteItems = useCallback(async (targets: FileBrowserItem[]) => {
    if (!entry) return;
    try {
      const token = requireAuth();
      const partnerCode = requirePartner().code;
      await Promise.all(targets.map(item => (
        item.type === 'folder' ?
          deleteSftpSubfolder({ token, partnerCode, entry, path: joinPath(path, item.name) }) :
          deleteSftpFile({ token, partnerCode, entry, path, filename: item.name })
      )));
      notify(targets.length > 1 ? `${targets.length} items deleted` : 'Deleted', { type: 'success' });
      await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to delete.', { type: 'error' });
    }
  }, [entry, path, notify, load]);

  const moveItems = useCallback(async (targets: FileBrowserItem[], destinationPath?: string) => {
    if (!entry) return;
    try {
      const token = requireAuth();
      const partnerCode = requirePartner().code;
      await Promise.all(targets.map(item => (
        item.type === 'folder' ?
          moveSftpSubfolder({ token, partnerCode, entry, path: joinPath(path, item.name), destinationPath: destinationPath ?? '' }) :
          moveSftpFile({ token, partnerCode, entry, path, filename: item.name, destinationPath })
      )));
      notify(targets.length > 1 ? `${targets.length} items moved` : 'Moved', { type: 'success' });
      await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to move.', { type: 'error' });
    }
  }, [entry, path, notify, load]);

  const uploadFiles = useCallback(async (files: File[]) => {
    if (!entry || files.length === 0) return;
    setIsUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    try {
      const token = requireAuth();
      const partnerCode = requirePartner().code;
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const contentType = file.type || 'application/octet-stream';
        const uploadUrl = await getSftpFileUploadUrl({
          token, partnerCode, entry, path, filename: file.name, contentType,
        });
        await uploadToS3({ presignedUrl: uploadUrl, file, contentType });
        setUploadProgress({ current: i + 1, total: files.length });
      }
      notify(files.length > 1 ? `${files.length} files uploaded` : 'File uploaded', { type: 'success' });
      await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to upload.', { type: 'error' });
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  }, [entry, path, notify, load]);

  const downloadItems = useCallback(async (targets: Extract<FileBrowserItem, { type: 'file' }>[]) => {
    if (!entry || targets.length === 0) return;
    try {
      const token = requireAuth();
      const partnerCode = requirePartner().code;
      const links = await getSftpFileDownloadUrls({
        token, partnerCode, entry, path, filenames: targets.map(t => t.name),
      });
      // Browsers only honor one auto-triggered download per tick — clicks fired
      // back-to-back in a loop collapse to just the last one, so stagger them.
      for (let i = 0; i < links.length; i += 1) {
        const { url, name } = links[i];
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = name;
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        if (i < links.length - 1) await new Promise(resolve => setTimeout(resolve, 300));
      }
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to download.', { type: 'error' });
    }
  }, [entry, path, notify]);

  return {
    entry,
    path,
    items,
    isLoading,
    error,
    isUploading,
    uploadProgress,
    breadcrumb,
    navigateTo,
    navigateInto,
    createFolder,
    renameItem,
    deleteItems,
    moveItems,
    uploadFiles,
    downloadItems,
    refresh: load,
  };
};
